// Live queries over the store: filter, search and sort a type, updated incrementally when a few records change.

import { comparator, compile, sortRecords, FIELD_TYPES, peopleFields, type Filter, type Id, type RecordObj, type SortKey } from '@confdeck/core-shared';
import type { Store, StoreEvent } from './store.ts';

export interface QuerySpec { typeId: Id; filter: Filter | null; sort: SortKey[]; search: string }

/** Lower-cased text of a record's searchable fields; cached per record revision. */
const haystacks = new WeakMap<RecordObj, string>();
function haystack(store: Store, r: RecordObj): string {
  let h = haystacks.get(r);
  if (h === undefined) {
    const parts: string[] = [];
    for (const f of peopleFields(store.meta.fields(r.typeId))) {
      const spec = FIELD_TYPES[f.fieldType];
      const v = r.data[f.key];
      if (v != null && spec.stored) parts.push(spec.search(v));
    }
    h = parts.join(' ').toLowerCase();
    haystacks.set(r, h);
  }
  return h;
}

const INCREMENTAL_LIMIT = 300;

/**
 * Result of one query. Holds a sorted id list; a change of up to a few hundred records is applied by
 * removing and binary-inserting them, so editing one cell in 100k rows never re-sorts everything.
 */
export class LiveQuery {
  ids: Id[] = [];
  version = 0;
  /** Time of the last full recompute, for the speed panel. */
  lastMs = 0;
  private member = new Set<Id>();
  private cmp: (a: RecordObj, b: RecordObj) => number;
  private listeners = new Set<() => void>();
  private unsub: () => void;
  private metaVersion: number;
  readonly store: Store;
  readonly spec: QuerySpec;

  constructor(store: Store, spec: QuerySpec) {
    this.store = store;
    this.spec = spec;
    this.cmp = this.makeCmp();
    this.metaVersion = store.metaVersion;
    this.recompute();
    this.unsub = store.subscribe((events) => this.onEvents(events));
  }

  private makeCmp() {
    const sort = this.spec.sort.length ? this.spec.sort : [{ field: 'createdAt', dir: 'desc' as const }];
    return comparator(this.store, this.spec.typeId, sort, (id) => this.store.meta.termRank(id));
  }

  private pred: ((r: RecordObj) => boolean) | null = null;

  private matches(r: RecordObj | undefined): r is RecordObj {
    if (!r || r.typeId !== this.spec.typeId) return false;
    if (this.pred && !this.pred(r)) return false;
    const q = this.spec.search.trim().toLowerCase();
    if (q && !q.split(/\s+/).every((w) => haystack(this.store, r).includes(w))) return false;
    return true;
  }

  recompute() {
    const t0 = performance.now();
    // Compiled per recompute: relation memos stay valid only for one pass over unchanged data.
    this.pred = this.spec.filter ? compile(this.store, this.spec.typeId, this.spec.filter) : null;
    const out: RecordObj[] = [];
    for (const id of this.store.recordIds(this.spec.typeId)) {
      const r = this.store.record(id);
      if (this.matches(r)) out.push(r);
    }
    sortRecords(this.store, this.spec.typeId, this.spec.sort.length ? this.spec.sort : [{ field: 'createdAt', dir: 'desc' }], out, (id) => this.store.meta.termRank(id));
    this.ids = out.map((r) => r.id);
    this.member = new Set(this.ids);
    this.lastMs = performance.now() - t0;
    this.version++;
  }

  private onEvents(events: StoreEvent[]) {
    if (this.store.metaVersion !== this.metaVersion) {
      // Schema changed (a field renamed, a stage reordered): rebuild comparator and result.
      this.metaVersion = this.store.metaVersion;
      this.cmp = this.makeCmp();
      this.recompute();
      return this.notify();
    }
    const touched = new Set<Id>();
    let full = false;
    for (const e of events) {
      if (e.scope !== 'records' || e.typeId !== this.spec.typeId) continue;
      if (!e.ids.length) { full = true; break; }
      for (const id of e.ids) touched.add(id);
    }
    if (!full && !touched.size) return;
    if (full || touched.size > INCREMENTAL_LIMIT) { this.recompute(); return this.notify(); }
    let changed = false;
    // Fresh predicate: memoised related-record checks must see the new data.
    this.pred = this.spec.filter ? compile(this.store, this.spec.typeId, this.spec.filter) : null;
    for (const id of touched) {
      const had = this.member.has(id);
      if (had) { this.ids.splice(this.ids.indexOf(id), 1); this.member.delete(id); changed = true; }
      const r = this.store.record(id);
      if (this.matches(r)) { this.insertSorted(r); changed = true; }
    }
    // A new array on every change: views that memoise on `ids` (the board's columns) must see a new value.
    if (changed) { this.ids = this.ids.slice(); this.version++; this.notify(); }
  }

  private insertSorted(r: RecordObj) {
    let lo = 0, hi = this.ids.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const other = this.store.record(this.ids[mid]!);
      if (other && this.cmp(other, r) < 0) lo = mid + 1; else hi = mid;
    }
    this.ids.splice(lo, 0, r.id);
    this.member.add(r.id);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private notify() { for (const fn of this.listeners) fn(); }
  dispose() { this.unsub(); this.listeners.clear(); }
}
